import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Download, RectangleHorizontal, Smartphone, Square } from 'lucide-react';
import { StillProvider } from '../components/alt/stillContext';
import { PlateArtboard, PlatePreview, VIEW_SIZES, type PlateRatio, type PlateView } from '../components/exports/CreatorFrame';
import { CREATOR_ASSETS } from '../components/exports/creatorAssets';
import { BRAND_ASSETS, EndCard, LowerThird } from '../components/exports/BrandMarks';
import { TOOL_PILLS, CRM_PILLS, SURFACE_PILLS } from '../components/alt/SurfacePill';
import { SurfacePills } from '../components/exports/SurfacePills';
import { useExportFrames } from '../components/exports/useExportFrames';
import { EXPORT_META } from '../components/exports/exportUi';
import { VIDEO_CLOSERS } from '../components/exports/videoClosers';

/**
 * THE CREATOR PACK, ON ITS OWN PAGE.
 *
 * It began as a section at the bottom of the screen library, which put the
 * vertical plates behind twenty landscape frames — the wrong order for the one
 * person it is for. Someone cutting a clip has a single job and should land on
 * the plates, in story order, with nothing else to scroll past.
 *
 * Three kinds of asset live here now, in the order they get reached for:
 * product plates, brand marks, and the two overlays every clip needs.
 *
 * The export machinery is shared rather than duplicated: `useExportFrames`
 * carries the clipboard fallback, the font-and-logo wait and the busy state
 * for this page and the screen library both.
 */

function RatioToggle({
  value,
  onChange,
  label
}: {
  value: PlateRatio;
  onChange: (r: PlateRatio) => void;
  label: string;
}) {
  return <div role="group" aria-label={label} className="inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-7772" data-mp="1nqv0uxao35gq">
      {([['9:16', Smartphone], ['16:9', RectangleHorizontal]] as const).map(([r, Icon]) => <button key={r} type="button" onClick={() => onChange(r)} aria-pressed={value === r} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-bold transition-colors ${value === r ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-7773" data-mp="vz5lj7wokxh4">
          <Icon size={12} data-id="element-7774" data-mp="34vji32rpvp9s" />
          {r}
        </button>)}
    </div>;
}

/**
 * WHICH OF THE THREE DELIVERABLES YOU ARE TAKING. Not a preview switch — the
 * export buttons follow it.
 */
function ViewToggle({
  value,
  onChange,
  label
}: {
  value: PlateView;
  onChange: (v: PlateView) => void;
  label: string;
}) {
  return <div role="group" aria-label={label} className="inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-7775" data-mp="2h5fozpc5n7x1">
      {([['overlay', 'Overlay', Square], ['9:16', '9:16', Smartphone], ['16:9', '16:9', RectangleHorizontal]] as const).map(([v, text, Icon]) => <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-bold transition-colors ${value === v ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-7776" data-mp="1eku4hl5ngq1">
          <Icon size={12} data-id="element-7777" data-mp="1lvakaozzhvvz" />
          {text}
        </button>)}
    </div>;
}
/**
 * The fixed box every preview is fitted into, whatever shape is selected. Tall
 * enough to show a 9:16 plate whole, wide enough for a 16:9 one, and never
 * smaller than a square shown at true size — so switching shapes moves the
 * plate and nothing else.
 */
const STAGE = {
  w: 620,
  h: 640
};

/** Checkerboard, so the artboard's transparency is visible rather than claimed. */
const CHECKS: React.CSSProperties = {
  backgroundImage: 'linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%),linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 8px 8px',
  backgroundColor: '#FFFFFF'
};
export function CreatorPack() {
  const {
    register,
    busy,
    copied,
    warm,
    copy,
    savePng,
    saveAllPng
  } = useExportFrames();
  /**
   * VIEW PICKS THE DELIVERABLE, NOT JUST THE PREVIEW.
   *
   * Each screen ships three ways — a square transparent overlay, a full 9:16
   * plate and a full 16:9 plate — because an overlay and a full-frame shot are
   * genuinely different assets. The toggle changes the file you get.
   */
  const [packView, setPackView] = useState<PlateView>('9:16');
  const [views, setViews] = useState<Record<string, PlateView>>({});
  const viewFor = (id: string) => views[id] ?? packView;
  const setAll = (v: PlateView) => {
    setPackView(v);
    // Clear the per-plate choices, or "set all" would visibly skip the plates
    // someone had already switched by hand.
    setViews({});
  };
  const [cardRatio, setCardRatio] = useState<PlateRatio>('9:16');
  return <main className="min-h-screen w-full bg-[#F4F1EA] pb-24" data-id="element-7778" data-mp="ikekyi7cwpt5">
      <header className="border-b border-[#E7E0D4] bg-[#FAF8F4]" data-id="element-7779" data-mp="imzn78bkh7rf">
        <div className="mx-auto max-w-[1240px] px-6 py-10" data-id="element-7780" data-mp="niohox8qbygv">
          <Link to="/app-screen-mockups/export-dashboards" className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#6B5D52] transition-colors hover:text-[#2B211A]" data-id="element-7781" data-mp="3xxxy75bffxf">
            <ArrowLeft size={13} data-id="element-7782" data-mp="dwdregrtf1a3" />
            All product screens
          </Link>

          <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-7783" data-mp="duasf78opxt8">
            For video
          </p>
          <h1 className="font-title mt-3 text-3xl font-semibold tracking-tight text-[#2B211A] md:text-[40px]" data-id="element-7784" data-mp="mwk1rts7vl51">
            Creator pack
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15.5px] leading-relaxed text-[#6B5D52]" data-id="element-7785" data-mp="iv89fghyklt5">
            Every product screen ships three ways: a square{' '}
            {VIEW_SIZES.overlay.out} overlay on a transparent artboard for
            dropping over your own footage, and full {VIEW_SIZES['9:16'].out}{' '}
            and {VIEW_SIZES['16:9'].out} plates that already sit on a
            video-looking ground, for cutting straight to. Pick the shape on
            any screen and the buttons hand you that file. Every product asset
            is a still PNG with the caption zone left clear, so you type in
            your editor rather than shipping words you cannot change — the two
            rendered closers further down are the only moving files here.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-7786" data-mp="1neu9wt3m8tdn">
            <button type="button" onClick={() => saveAllPng(CREATOR_ASSETS.map(a => {
            const v = viewFor(a.id);
            return {
              id: a.id,
              name: `${a.id}-${v === 'overlay' ? 'square' : v.replace(':', 'x')}`
            };
          }))} disabled={busy !== null} className="inline-flex items-center gap-2 rounded-lg bg-[#EA580C] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#C2410C] disabled:opacity-60" data-id="element-7787" data-mp="1sdot3w5hwjya">
              <Download size={14} data-id="element-7788" data-mp="byvvsatkzh5k" />
              {busy ? 'Exporting…' : `Download all ${CREATOR_ASSETS.length}`}
            </button>
            <ViewToggle value={packView} onChange={setAll} label="Set every shape" data-id="element-7789" data-mp="4fmk2axlr6dc" />
          </div>

          <p className="mt-4 max-w-[64ch] text-[12.5px] leading-relaxed text-neutral-500" data-id="element-7790" data-mp="3vtsgqvh9irzh">
            Keep this window at desktop width — the product screens inside these
            plates render their desktop layout above 1024px, and the export
            captures what is actually on screen. Every plate is held on its
            finished state, so nothing arrives mid-animation.
          </p>
        </div>
      </header>

      {/* ── Product plates ───────────────────────────────────────────────────
       ROW PER SCREEN, PREVIEW FIRST.
       Stacked in a grid, every plate pushed its name, note, toggle, caption
       and both buttons above the image — so on a 960-wide landscape plate you
       scrolled through a paragraph before seeing anything, and the previews
       never lined up because the captions differ in length. The preview now
       leads, inside a stage of constant size, and the whole control set sits
       in a fixed column beside it — so the page holds still as you compare.
       ──────────────────────────────────────────────────────────────────────*/}
      <div className="mx-auto max-w-[1400px] px-6 pt-14" data-id="element-7791" data-mp="39vfwx4yo2hag">
        <div className="flex flex-col gap-14" data-id="element-7792" data-mp="39x0ruvow828u">
          {CREATOR_ASSETS.map(asset => {
          const view = viewFor(asset.id);
          const size = VIEW_SIZES[view];
          const overlay = view === 'overlay';
          // Never enlarge: a square at true size is already the right reading
          // size, and scaling it up would only soften it.
          const fit = Math.min(STAGE.w / size.w, STAGE.h / size.h, 1);
          const copiedNow = copied === asset.id;
          const busyNow = busy === asset.id;
          const plate = <StillProvider data-id="element-7793" data-mp="oese97gijyix">
                {overlay ? <PlateArtboard width={asset.width} renderWidth={asset.renderWidth} data-id="element-7794" data-mp="oe0uctwa3p8h">
                    {asset.render()}
                  </PlateArtboard> : <PlatePreview ratio={view} width={asset.width} renderWidth={asset.renderWidth} data-id="element-7795" data-mp="2dw62ullwhwt4">
                    {asset.render()}
                  </PlatePreview>}
              </StillProvider>;
          return <section key={asset.id} className="flex flex-col gap-6 border-t border-[#E7E0D4] pt-8 lg:flex-row lg:items-start lg:gap-10" data-id="element-7796" data-mp="2nlzfzr1oyjzh">
                {/* A STAGE OF CONSTANT SIZE.
                 Rendering each shape at its native pixel size meant switching
                 from 16:9 to 9:16 nearly doubled the row's height and shunted
                 everything below it down the page — the layout lurched every
                 time you compared two shapes. The stage now stays put and the
                 plate is fitted inside it, so only the plate changes. */}
                <div className="flex flex-none items-center justify-center" style={{
              width: STAGE.w,
              height: STAGE.h
            }} data-id="element-7797" data-mp="1zz11gyx4tm47">
                  <div className="overflow-hidden rounded-[18px] border border-[#E7E0D4]" style={{
                width: size.w * fit,
                height: size.h * fit,
                ...(overlay ? CHECKS : null)
              }} data-id="element-7798" data-mp="3bb1skkhgxmn4">
                    <div className="origin-top-left" style={{
                  width: size.w,
                  height: size.h,
                  transform: `scale(${fit})`
                }} data-id="element-7799" data-mp="1bzzjjxbfggop">
                      {plate}
                    </div>
                  </div>
                </div>

                {/* THE CAPTURED NODE, at true pixel size and parked off-canvas.
                 The capture reads real bounding boxes, so it cannot live under
                 the stage's transform — a scaled ancestor would hand back a
                 scaled file. It stays mounted rather than conditional so the
                 export never depends on what is on screen.
                  `isolate` because these screens carry absolute layers that
                 would otherwise resolve against the page. */}
                <div ref={register(asset.id)} aria-hidden="true" className="pointer-events-none fixed -left-[9999px] top-0 isolate overflow-hidden" style={{
              width: size.w,
              height: size.h,
              ...(overlay ? CHECKS : null)
            }} data-id="element-7800" data-mp="2l6hwdg68shrk">
                  {plate}
                </div>

                <div className="min-w-0 lg:sticky lg:top-8 lg:max-w-[380px]" data-id="element-7801" data-mp="2zvxp31pc2x9w">
                  <h2 className="font-title text-[21px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7802" data-mp="227nlwjdta3h7">
                    {asset.name}
                  </h2>
                  <p className="mt-1 text-[12.5px] text-neutral-500" data-id="element-7803" data-mp="2778r3bjkr82d">
                    {asset.note}
                  </p>

                  <div className="mt-4 flex flex-wrap items-center gap-2" onPointerEnter={() => warm(asset.id)} onFocus={() => warm(asset.id)} data-id="element-7804" data-mp="2c868vn0oa03c">
                    <button type="button" onClick={() => savePng(asset.id, 2, `${asset.id}-${overlay ? 'square' : view.replace(':', 'x')}`)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-7805" data-mp="3njxbxqjiy3j7">
                      <Download size={13} data-id="element-7806" data-mp="1nihbtcquh6mm" />
                      {busyNow ? 'Preparing…' : `PNG ${size.out}`}
                    </button>
                    <button type="button" onClick={() => copy(asset.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-7807" data-mp="1xh6j6bl478om">
                      {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-7808" data-mp="1ik6355cad25z" /> : <Copy size={13} data-id="element-7809" data-mp="1ikxntl4chfhh" />}
                      {copiedNow ? 'Copied' : 'Copy for Figma'}
                    </button>
                  </div>

                  <div className="mt-3" data-id="element-7810" data-mp="q2jy68t5wzth">
                    <ViewToggle value={view} onChange={v => setViews(m => ({
                  ...m,
                  [asset.id]: v
                }))} label={`${asset.name} shape`} data-id="element-7811" data-mp="q1ini5g26fca" />
                  </div>

                  <p className="mt-4 text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-7812" data-mp="bvqeiwkhxuvj">
                    {asset.use}
                  </p>
                </div>
              </section>;
        })}
        </div>
      </div>

      {/* ── Rendered closers ─────────────────────────────────────────────── */}
      <div className="mx-auto mt-20 max-w-[1360px] px-6" data-id="element-7813" data-mp="6wm6wad8057b">
        <div className="border-b border-[#E7E0D4] pb-3" data-id="element-7814" data-mp="upcgwe1kceey">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-7815" data-mp="zndwgozs5vlo">
            Video
          </p>
          <h2 className="font-title mt-1.5 text-[26px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7816" data-mp="kqmz0l9o2typ">
            The closers
          </h2>
          <p className="mt-2 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-7817" data-mp="pndaevaj11ds">
            The animated tail, in both shapes. Handed over as the original MP4
            rather than re-encoded here, so you drop the source file straight
            on the end of your timeline. Everything else on this page is a
            still — these are the only moving assets.
          </p>
        </div>

        <div className="mt-8 grid gap-x-8 gap-y-10 lg:grid-cols-2" data-id="element-7818" data-mp="3luk5g6k71tjx">
          {VIDEO_CLOSERS.map(closer => <section key={closer.id} data-id="element-7819" data-mp="3gx1te7avqy7p">
              <h3 className="font-title text-[19px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7820" data-mp="3bxxj1dc7yufs">
                {closer.name}
              </h3>
              <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-7821" data-mp="dfjkrxna9n68">{closer.note}</p>
              <div className="mt-4" data-id="element-7822" data-mp="8i08v4e25mhg">
                <a href={closer.src} download className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-7823" data-mp="8evuxfasjcdl">
                  <Download size={13} data-id="element-7824" data-mp="3uq9le02vhvpx" />
                  Download MP4
                </a>
              </div>
              <p className="mt-4 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-7825" data-mp="2o5qi00vjtza1">
                {closer.use}
              </p>
              {/* Fitted into the same stage the plates use, so a vertical and a
               landscape closer sit side by side without one towering over the
               other. Controls rather than autoplay: this is an asset to
               inspect, not a page that should start moving on its own. */}
              <div className="mt-4 flex items-center justify-center rounded-[18px] border border-[#E7E0D4] bg-[#14100D] p-3" style={{
            height: STAGE.h * 0.62
          }} data-id="element-7826" data-mp="1dgx4c9jjzcru">
                <video src={closer.src} controls playsInline preload="metadata" className="h-full w-auto rounded-lg" data-id="element-7827" data-mp="1mg0m9a6zhm4r" />
              </div>
            </section>)}
        </div>
      </div>

      {/* ── End card and overlays ────────────────────────────────────────── */}
      <div className="mx-auto mt-20 max-w-[1360px] px-6" data-id="element-7828" data-mp="1wcdhwdm3aa25">
        <div className="border-b border-[#E7E0D4] pb-3" data-id="element-7829" data-mp="3t8b7e6aeidzo">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-7830" data-mp="20gju1t565y4">
            Overlays
          </p>
          <h2 className="font-title mt-1.5 text-[26px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7831" data-mp="3q6g60yo1vanw">
            The two things every clip needs
          </h2>
          <p className="mt-2 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-7832" data-mp="3y6ti2z8j1ff">
            An end card for the last two seconds, and a lower third to name who
            is talking. Both get rebuilt by hand in every editor, which is why
            no two clips from the team look alike — take these instead.
          </p>
        </div>

        <div className="mt-8 grid gap-x-8 gap-y-12 lg:grid-cols-2" data-id="element-7833" data-mp="8wi3qetfzdy6">
          <section data-id="element-7834" data-mp="mp7qilgsomwc">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-7835" data-mp="mnmussiquxww">
              <div data-id="element-7836" data-mp="hp3a0vfvzs64">
                <h3 className="font-title text-[19px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7837" data-mp="hqmqam1jm37w">
                  End card
                </h3>
                <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-7838" data-mp="wkhs583a6s9l">
                  The closer's last frame · {VIEW_SIZES[cardRatio].out}
                </p>
              </div>
              <RatioToggle value={cardRatio} onChange={setCardRatio} label="End card ratio" data-id="element-7839" data-mp="1bace0hes1t80" />
            </div>
            <p className="mt-2 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-7840" data-mp="1f91zkx9yyh2q">
              Exactly where the closer above lands, held as a still — same
              ground, same face, same address. Use it where a video tail cannot
              go: a thumbnail, a slide, a static post. Two seconds is enough.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2" onPointerEnter={() => warm('end-card')} onFocus={() => warm('end-card')} data-id="element-7841" data-mp="wctr7klntq37">
              <button type="button" onClick={() => savePng('end-card')} disabled={busy === 'end-card'} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-7842" data-mp="imqf3mghvcrh">
                <Download size={13} data-id="element-7843" data-mp="dkr9s71725rw" />
                {busy === 'end-card' ? 'Preparing…' : `PNG ${VIEW_SIZES[cardRatio].out}`}
              </button>
            </div>
            {/* The one full-frame asset here: an end card fills the screen by
             definition, so it ships as a whole 9:16 or 16:9 plate rather than
             as a square overlay. */}
            <div ref={register('end-card')} className="relative isolate mt-4 w-fit overflow-hidden rounded-[18px] border border-[#E7E0D4]" style={{
            width: VIEW_SIZES[cardRatio].w,
            height: VIEW_SIZES[cardRatio].h
          }} data-id="element-7844" data-mp="f4b1qhsatfcq">
              <EndCard ratio={cardRatio} data-id="element-7845" data-mp="5coji1hjmg0h" />
            </div>
          </section>

          <section data-id="element-7846" data-mp="1dpfcj3myna3">
            <h3 className="font-title text-[19px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7847" data-mp="1d5vgwufdotf">
              Lower third
            </h3>
            <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-7848" data-mp="3njup506ag3jv">
              Name strip · transparent artboard
            </p>
            <p className="mt-2 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-7849" data-mp="3ikhtpr1cpo5o">
              Drops straight over footage — the artboard is genuinely
              transparent, so there is no plate edge to hide. Retype the name
              and role in Figma, or take the PNG and cover the text.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2" onPointerEnter={() => warm('lower-third')} onFocus={() => warm('lower-third')} data-id="element-7850" data-mp="3ifjwnas1pmsc">
              <button type="button" onClick={() => savePng('lower-third', 4)} disabled={busy === 'lower-third'} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-7851" data-mp="2pqlcy989yckx">
                <Download size={13} data-id="element-7852" data-mp="2zmobz67txv3n" />
                {busy === 'lower-third' ? 'Preparing…' : 'PNG 4×'}
              </button>
              <button type="button" onClick={() => copy('lower-third')} disabled={busy === 'lower-third'} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-7853" data-mp="270ddq27p46a0">
                {copied === 'lower-third' ? <Check size={13} className="text-emerald-600" data-id="element-7854" data-mp="czex3j46xs5v" /> : <Copy size={13} data-id="element-7855" data-mp="i0cevulagk6u" />}
                {copied === 'lower-third' ? 'Copied' : 'Copy for Figma'}
              </button>
            </div>
            {/* Checkerboard, so the transparency is visible rather than
             claimed — the strip is meant to sit on footage. */}
            <div className="mt-4 flex w-fit items-center justify-center rounded-[18px] border border-[#E7E0D4] p-10" style={CHECKS} data-id="element-7856" data-mp="3leypyh0y98ke">
              <span ref={register('lower-third')} className="relative isolate inline-flex" data-id="element-7857" data-mp="3uh7veshd2bqx">
                <LowerThird data-id="element-7858" data-mp="33bu6b1ilphv" />
              </span>
            </div>
          </section>
        </div>
      </div>

      {/* ── Brand marks ──────────────────────────────────────────────────── */}
      <div className="mx-auto mt-20 max-w-[1360px] px-6" data-id="element-7859" data-mp="36rdkigglqx9b">
        <div className="border-b border-[#E7E0D4] pb-3" data-id="element-7860" data-mp="36psr4u60xkd1">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-7861" data-mp="2z7bcyan7dhdq">
            Marks
          </p>
          <h2 className="font-title mt-1.5 text-[26px] font-semibold tracking-tight text-[#2B211A]" data-id="element-7862" data-mp="2u9t1achswo21">
            Giga, and everything that connects to it
          </h2>
          <p className="mt-2 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-7863" data-mp="2l9ed95dgcuuu">
            Giga's own marks on transparent artboards at 4×, since a mark gets
            placed small and scaled by whoever uses it. Everything Giga
            connects to follows underneath, as lockups.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-7864" data-mp="26k8grp601iqn">
          {BRAND_ASSETS.map(mark => <div key={mark.id} className="rounded-2xl border border-[#E7E0D4] bg-white p-4" onPointerEnter={() => warm(mark.id)} data-id="element-7865" data-mp="22jokent92j5k">
              <div className="flex items-center justify-center rounded-xl" style={{
            height: 132,
            backgroundColor: mark.onDark ? '#2B211A' : '#FFFFFF',
            backgroundImage: mark.onDark ? undefined : 'linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%),linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%)',
            backgroundSize: '14px 14px',
            backgroundPosition: '0 0, 7px 7px'
          }} data-id="element-7866" data-mp="1xkv4koqhqg28">
                <span ref={register(mark.id)} className="inline-flex items-center justify-center" style={{
              width: mark.boxW,
              height: mark.boxH
            }} data-id="element-7867" data-mp="1dzbid9v5hu6g">
                  {mark.render()}
                </span>
              </div>
              <p className="font-title mt-3 text-[15px] font-bold text-[#2B211A]" data-id="element-7868" data-mp="1rw8fft1v47vs">{mark.name}</p>
              <p className={`${EXPORT_META} mt-0.5`} data-id="element-7869" data-mp="1wus4i2br5zsd">{mark.note}</p>
              <button type="button" onClick={() => savePng(mark.id, 4)} disabled={busy === mark.id} className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-7870" data-mp="2kl5mf00mii98">
                <Download size={13} data-id="element-7871" data-mp="8xivz0c86fh4" />
                {busy === mark.id ? 'Preparing…' : 'PNG 4×'}
              </button>
            </div>)}
        </div>

      </div>

      {/* ── Lockups ──────────────────────────────────────────────────────────
       THE SAME GRID THE SCREEN LIBRARY USES.
       The pack shipped bare marks in a dense grid, which hands over a logo but
       not the thing anyone places on a clip: the mark WITH its name, in the
       tone the footage needs. This is the screen library's component, imported
       rather than copied — capsule or bare, for light or dark, PNG, SVG and
       Figma paste on every one.
       ──────────────────────────────────────────────────────────────────────*/}
      <div className="mx-auto mt-20 max-w-[1360px] space-y-14 px-6" data-id="element-7872" data-mp="1fckxmgnuex86">
        <SurfacePills heading="Tool lockups" title="Connected tool mark and name" note="Reach for these when a clip names a tool — “Giga reads your Salesforce” lands harder with the tool's own lockup on screen than with the words alone. Marks come from the shared brand registry, so they match the connections map exactly." items={TOOL_PILLS} data-id="element-7873" data-mp="1bbra3oebbkft" />
        <SurfacePills heading="CRM lockups" title="CRM mark and name" note="The CRMs a viewer is most likely to already run. Use the one your audience uses; a clip that names their CRM is a clip about them." items={CRM_PILLS} data-id="element-7874" data-mp="2fpqhu6l7zevt" />
        <SurfacePills heading="Surface lockups" title="Platform mark and name" note="Where Giga answers — Slack, Teams, ChatGPT and the rest. The capsule is translucent, so whatever sits behind it still shows through." items={SURFACE_PILLS} data-id="element-7875" data-mp="2tn6p1kbcbcom" />
      </div>
    </main>;
}